/**
 * Enhanced Project Profitability Service
 * Provides comprehensive financial tracking and profitability analysis for projects
 */

import { supabase } from '@/integrations/supabase/client';

export interface ProjectProfitability {
  project_id: string;
  total_revenue: number;
  milestone_revenue: number;
  invoice_revenue: number;
  total_expenses: number;
  total_time_costs: number;
  total_team_costs: number;
  overhead_allocation: number;
  gross_profit: number;
  net_profit: number;
  margin_percentage: number;
  last_calculated: string;
}

export interface ProfitabilityBreakdown {
  revenue: {
    milestones: number;
    invoices: number;
    total: number;
  };
  costs: {
    direct_expenses: number;
    time_costs: number;
    team_payments: number;
    overhead: number;
    total: number;
  };
  profit: {
    gross: number;
    net: number;
    margin: number;
  };
}

export interface ProjectFinancialMetrics {
  budget_utilization: number;
  cost_per_hour: number;
  revenue_per_hour: number;
  efficiency_ratio: number;
  burn_rate: number;
  projected_completion_cost: number;
}

class EnhancedProfitabilityService {
  /**
   * Calculate comprehensive project profitability
   */
  async calculateProjectProfitability(projectId: string): Promise<ProjectProfitability | null> {
    const { data, error } = await supabase
      .rpc('calculate_enhanced_project_profitability', {
        project_id: projectId
      });

    if (error) {
      throw error;
    }

    return data?.[0] || null;
  }

  /**
   * Get detailed profitability breakdown
   */
  async getProfitabilityBreakdown(projectId: string): Promise<ProfitabilityBreakdown> {
    const profitability = await this.calculateProjectProfitability(projectId);
    
    if (!profitability) {
      throw new Error('Could not calculate profitability for project');
    }

    return {
      revenue: {
        milestones: profitability.milestone_revenue,
        invoices: profitability.invoice_revenue,
        total: profitability.total_revenue
      },
      costs: {
        direct_expenses: profitability.total_expenses,
        time_costs: profitability.total_time_costs,
        team_payments: profitability.total_team_costs,
        overhead: profitability.overhead_allocation,
        total: profitability.total_expenses + profitability.total_time_costs + 
               profitability.total_team_costs + profitability.overhead_allocation
      },
      profit: {
        gross: profitability.gross_profit,
        net: profitability.net_profit,
        margin: profitability.margin_percentage
      }
    };
  }

  /**
   * Calculate financial metrics for project management
   */
  async getProjectFinancialMetrics(projectId: string): Promise<ProjectFinancialMetrics> {
    // Get project details
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('budget, hourly_rate, start_date, end_date')
      .eq('id', projectId)
      .single();

    if (projectError) throw projectError;

    // Get total hours logged
    const { data: timeEntries, error: timeError } = await supabase
      .from('project_time_entries')
      .select('hours_logged, billing_rate')
      .eq('project_id', projectId)
      .eq('status', 'approved');

    if (timeError) throw timeError;

    const totalHours = timeEntries?.reduce((sum, entry) => sum + entry.hours_logged, 0) || 0;
    const totalTimeCosts = timeEntries?.reduce((sum, entry) => 
      sum + (entry.hours_logged * (entry.billing_rate || 0)), 0) || 0;

    // Get profitability data
    const profitability = await this.calculateProjectProfitability(projectId);
    const totalCosts = profitability ? 
      profitability.total_expenses + profitability.total_time_costs + 
      profitability.total_team_costs + profitability.overhead_allocation : 0;

    const budgetUtilization = project.budget > 0 ? (totalCosts / project.budget) * 100 : 0;
    const costPerHour = totalHours > 0 ? totalCosts / totalHours : 0;
    const revenuePerHour = totalHours > 0 ? (profitability?.total_revenue || 0) / totalHours : 0;
    const efficiencyRatio = costPerHour > 0 ? revenuePerHour / costPerHour : 0;

    // Calculate burn rate (cost per day)
    const projectDays = project.start_date && project.end_date ? 
      Math.ceil((new Date(project.end_date).getTime() - new Date(project.start_date).getTime()) / (1000 * 60 * 60 * 24)) : 1;
    const burnRate = projectDays > 0 ? totalCosts / projectDays : 0;

    // Project completion cost based on current burn rate
    const daysRemaining = project.end_date ? 
      Math.max(0, Math.ceil((new Date(project.end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) : 0;
    const projectedCompletionCost = totalCosts + (burnRate * daysRemaining);

    return {
      budget_utilization: budgetUtilization,
      cost_per_hour: costPerHour,
      revenue_per_hour: revenuePerHour,
      efficiency_ratio: efficiencyRatio,
      burn_rate: burnRate,
      projected_completion_cost: projectedCompletionCost
    };
  }

  /**
   * Get profitability comparison across projects
   */
  async getProjectProfitabilityComparison(organizationId?: string): Promise<ProjectProfitability[]> {
    let query = supabase
      .from('projects')
      .select('id');

    if (organizationId) {
      query = query.eq('organization_id', organizationId);
    }

    const { data: projects, error } = await query;
    if (error) throw error;

    const profitabilityPromises = projects.map(project => 
      this.calculateProjectProfitability(project.id)
    );

    const results = await Promise.all(profitabilityPromises);
    return results.filter(result => result !== null) as ProjectProfitability[];
  }

  /**
   * Track cost overruns and budget alerts
   */
  async checkBudgetAlerts(projectId: string): Promise<{
    is_over_budget: boolean;
    budget_utilization: number;
    cost_overrun: number;
    projected_overrun: number;
    alert_level: 'green' | 'yellow' | 'red';
  }> {
    const metrics = await this.getProjectFinancialMetrics(projectId);
    const budgetUtilization = metrics.budget_utilization;
    
    let alertLevel: 'green' | 'yellow' | 'red' = 'green';
    if (budgetUtilization > 100) {
      alertLevel = 'red';
    } else if (budgetUtilization > 80) {
      alertLevel = 'yellow';
    }

    const { data: project } = await supabase
      .from('projects')
      .select('budget')
      .eq('id', projectId)
      .single();

    const budget = project?.budget || 0;
    const profitability = await this.calculateProjectProfitability(projectId);
    const actualCosts = profitability ? 
      profitability.total_expenses + profitability.total_time_costs + 
      profitability.total_team_costs + profitability.overhead_allocation : 0;

    const costOverrun = Math.max(0, actualCosts - budget);
    const projectedOverrun = Math.max(0, metrics.projected_completion_cost - budget);

    return {
      is_over_budget: budgetUtilization > 100,
      budget_utilization: budgetUtilization,
      cost_overrun: costOverrun,
      projected_overrun: projectedOverrun,
      alert_level: alertLevel
    };
  }

  /**
   * Generate financial summary for dashboard
   */
  async getFinancialSummary(organizationId?: string): Promise<{
    total_projects: number;
    total_revenue: number;
    total_costs: number;
    total_profit: number;
    average_margin: number;
    projects_over_budget: number;
    top_performing_projects: { project_id: string; project_name: string; margin: number; }[];
  }> {
    const profitabilityData = await this.getProjectProfitabilityComparison(organizationId);
    
    // Get project names
    const projectIds = profitabilityData.map(p => p.project_id);
    const { data: projects } = await supabase
      .from('projects')
      .select('id, name')
      .in('id', projectIds);

    const projectMap = new Map(projects?.map(p => [p.id, p.name]) || []);

    const summary = profitabilityData.reduce((acc, curr) => {
      acc.total_revenue += curr.total_revenue;
      acc.total_costs += curr.total_expenses + curr.total_time_costs + 
                        curr.total_team_costs + curr.overhead_allocation;
      acc.total_profit += curr.net_profit;
      acc.margin_sum += curr.margin_percentage;
      
      if (curr.margin_percentage < 0) {
        acc.projects_over_budget++;
      }
      
      return acc;
    }, {
      total_revenue: 0,
      total_costs: 0,
      total_profit: 0,
      margin_sum: 0,
      projects_over_budget: 0
    });

    const averageMargin = profitabilityData.length > 0 ? 
      summary.margin_sum / profitabilityData.length : 0;

    // Top performing projects by margin
    const topProjects = profitabilityData
      .map(p => ({
        project_id: p.project_id,
        project_name: projectMap.get(p.project_id) || 'Unknown',
        margin: p.margin_percentage
      }))
      .sort((a, b) => b.margin - a.margin)
      .slice(0, 5);

    return {
      total_projects: profitabilityData.length,
      total_revenue: summary.total_revenue,
      total_costs: summary.total_costs,
      total_profit: summary.total_profit,
      average_margin: averageMargin,
      projects_over_budget: summary.projects_over_budget,
      top_performing_projects: topProjects
    };
  }
}

export const enhancedProfitabilityService = new EnhancedProfitabilityService();
export default enhancedProfitabilityService;